import { Link, Text } from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/table-core";
import { useMemo } from "react";
import { RiHistoryLine } from "react-icons/ri";
import { Link as RouterLink } from "react-router-dom";
import { StatusTag } from "src/components/DataDisplay/StatusTag";
import { NoDataTable } from "src/components/Feedback/NoDataTable";
import { PaginatedTable } from "src/components/Table/PaginatedTable";
import { SortType } from "src/components/Table/SortButton";
import { TextWithOverflowTooltip } from "src/components/TextWithOverflowTooltip";
import { BulkActionItem } from "src/generated/hooks/form-service/requests/types.gen";
import { useOrganization } from "src/hooks/useOrganization";
import * as Url from "src/services/url";
import { getItemStatusMetadata } from "./labels";

interface ActivityItemListProps {
  items: BulkActionItem[];
  formTemplateId: string;
  limit: number;
  offset: number;
  count: number;
  errorCount: number;
  hasActiveFilters: boolean;
  onFetchMore: (limit: number, offset: number) => void;
  onChangeSort: (columnName: string, sortType: SortType) => void;
  onClearFilters: () => void;
  isLoading?: boolean;
}

const SORTABLE_COLUMN_IDS = [
  "first_name",
  "last_name",
  "school",
  "status",
  "message",
];

export const ActivityItemList: React.FC<ActivityItemListProps> = ({
  items,
  formTemplateId,
  limit,
  offset,
  count,
  errorCount,
  hasActiveFilters,
  onFetchMore,
  onChangeSort,
  onClearFilters,
  isLoading = false,
}) => {
  const organization = useOrganization();

  const columns: ColumnDef<BulkActionItem>[] = useMemo(() => {
    const formUrl = (item: BulkActionItem) =>
      Url.OrgAdmin.Forms.view({
        organization,
        formTemplateId,
        id: item.formId,
      });

    const studentNameCell = (item: BulkActionItem, name: string | null) => (
      <Link
        as={RouterLink}
        color="primary.500"
        textDecoration="underline"
        to={formUrl(item)}
      >
        {name ?? ""}
      </Link>
    );

    return [
      {
        id: "first_name",
        header: "STUDENT FIRST NAME",
        cell: (props) =>
          studentNameCell(
            props.row.original,
            props.row.original.student.firstName
          ),
      },
      {
        id: "last_name",
        header: "STUDENT LAST NAME",
        cell: (props) =>
          studentNameCell(
            props.row.original,
            props.row.original.student.lastName
          ),
      },
      {
        id: "school",
        header: "SCHOOL",
        cell: (props) => (
          <TextWithOverflowTooltip
            content={props.row.original.school?.name ?? ""}
            maxWidth="20em"
          />
        ),
      },
      {
        id: "status",
        header: "STATUS",
        cell: (props) => (
          <StatusTag
            disableGlossary
            status={getItemStatusMetadata(props.row.original.status)}
          />
        ),
      },
      {
        id: "message",
        header: "ERROR",
        cell: (props) => (
          <TextWithOverflowTooltip
            content={props.row.original.message ?? ""}
            maxWidth="55rem"
          />
        ),
      },
    ];
  }, [formTemplateId, organization]);

  const tableHeader = () => (
    <Text textTransform="uppercase" fontSize="xs" fontWeight="bold">
      Items: {count}
      {errorCount > 0 && ` · ${errorCount} failed`}
    </Text>
  );

  if (isLoading) {
    return (
      <PaginatedTable<BulkActionItem>
        data={[]}
        columns={columns}
        limit={limit}
        offset={offset}
        count={0}
        onFetchMore={onFetchMore}
        sortableColumnIds={SORTABLE_COLUMN_IDS}
        onChangeSort={onChangeSort}
        isLoading={true}
        tableHeader={tableHeader}
      />
    );
  }

  if (!items.length) {
    return (
      <NoDataTable
        icon={RiHistoryLine}
        text="No records found"
        buttonText={hasActiveFilters ? "Clear" : undefined}
        onButtonAction={onClearFilters}
      />
    );
  }

  return (
    <PaginatedTable<BulkActionItem>
      data={items}
      columns={columns}
      limit={limit}
      offset={offset}
      count={count}
      onFetchMore={onFetchMore}
      sortableColumnIds={SORTABLE_COLUMN_IDS}
      onChangeSort={onChangeSort}
      tableHeader={tableHeader}
    />
  );
};
